import classNames from 'classnames';
import {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    type HTMLAttributes,
    type ReactNode,
    type UIEventHandler,
} from 'react';
import styles from './FJScroller.module.less';

type FJScrollerAxis = 'y' | 'x' | 'both';

/** 连续滚动节之间的静止判定（ms），超过后下一次 scroll 视为新一节并再次触发 onScrollTriggerStart */
const SCROLL_START_IDLE_MS = 150;

interface FJScrollerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    children?: ReactNode;
    /** 滚动方向，默认纵向 */
    axis?: FJScrollerAxis;
    /** 滚动时触发（与原生 `onScroll` 都会收到事件） */
    onScrollTrigger?: UIEventHandler<HTMLDivElement>;
    /**
     * 一段连续滚动中**首次**产生滚动时触发；与下一节之间以 {@link SCROLL_START_IDLE_MS} 级静止区分（非浏览器原生事件）
     */
    onScrollTriggerStart?: UIEventHandler<HTMLDivElement>;
}

const FJScroller = forwardRef<HTMLDivElement, FJScrollerProps>(function FJScroller(
    { className, axis = 'y', children, onScroll, onScrollTrigger, onScrollTriggerStart, ...rest },
    ref,
) {
    const scrollSessionRef = useRef(false);
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (idleTimerRef.current != null) {
                clearTimeout(idleTimerRef.current);
            }
        },
        [],
    );

    const handleScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        e => {
            onScroll?.(e);
            onScrollTrigger?.(e);

            if (idleTimerRef.current != null) {
                clearTimeout(idleTimerRef.current);
            }
            if (!scrollSessionRef.current) {
                scrollSessionRef.current = true;
                onScrollTriggerStart?.(e);
            }
            idleTimerRef.current = setTimeout(() => {
                scrollSessionRef.current = false;
                idleTimerRef.current = null;
            }, SCROLL_START_IDLE_MS);
        },
        [onScroll, onScrollTrigger, onScrollTriggerStart],
    );

    return (
        <div
            ref={ref}
            className={classNames(styles.root, styles[`axis_${axis}`], className)}
            onScroll={handleScroll}
            {...rest}
        >
            {children}
        </div>
    );
});

export default FJScroller;
